// @ts-strict-ignore
import { indexActionsByPublicId, isActionOfType } from 'flow/service/actions';
import { getBrowserOS } from 'flow/service/util';
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  ActionOption,
  ServiceObject,
  ServicesByName,
} from 'flow/store/engine/types';
// BUILD-4687: disabling temporarily
// import { generatePublicId } from 'flow/service/publicIds';
import { WEBHOOK_KEY } from 'flow/service/actionOptions';
import { makeServiceConfig } from 'flow/service/services/serviceConfigs';
// import type { ServiceConfig, ParsedOption } from '~/dashboard/views/settings/service-configuration/types';

/**
 * Checks if the event target is an input element (input, textarea, or contenteditable)
 */
export function isTargetInputElement(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;

  if (!target) {
    return false;
  }

  return target.tagName === 'INPUT'
    || target.tagName === 'TEXTAREA'
    || target.isContentEditable;
}

/**
 * Returns the keyboard shortcuts for copy and paste based on the user's operating system
 */
export function getKeyboardShortcuts(): {
  copyShortcut: string;
  pasteShortcut: string;
  diffShortcut: string;
} {
  const browserOS = getBrowserOS();
  const isMac = browserOS === 'mac';

  return {
    copyShortcut: isMac ? '⌘ C' : 'Ctrl C',
    pasteShortcut: isMac ? '⌘ V' : 'Ctrl V',
    diffShortcut: isMac ? '⌘ /' : 'Ctrl /',
  };
}

/**
 * Filters out auth/secret options and proprietary options from service action options
 */
export function getScrubbedServiceActionOptions(args: {
  options: Record<string, ActionOption>;
  service: ServiceObject;
}): Record<string, ActionOption> {
  const { options, service } = args;

  // BUILD-4687: Reverted to read `service.serviceConfigOptions` (populated by
  // the restored customerServiceConfigOptions batch call) since
  // `service.serviceConfig` is temporarily no longer prefetched for each service.
  const matchingServiceConfigOptions = service?.serviceConfigOptions ?? [];
  const authOrSecretKeys = new Set(
    matchingServiceConfigOptions
      .filter(option => option.category === 'auth' || option.isSecret)
      .map(option => option.slug),
  );

  const scrubbedOptions = Object
    .entries(options)
    .filter(([name]) => {
      if (name === WEBHOOK_KEY) return false; // WEBHOOK_KEY is a proprietary option
      if (authOrSecretKeys.has(name)) return false;
      return true;
    });

  return Object.fromEntries(scrubbedOptions);
}

/**
 * removes specific service action options that are:
 * 1. proprietary to the account
 * 2. auth or secret
 */
export function scrubServiceActions(args: {
  actions: Record<string, Action>;
  services: ServicesByName;
}): Record<Action['publicId'], Action> {
  const { actions, services } = args;

  const scrubbedActions = Object.values(actions).map(action => {
    // do not modify non-service actions
    if (!isActionOfType(action, ActionType.SERVICE)) {
      return action;
    }

    return {
      ...action,
      options: getScrubbedServiceActionOptions({
        options: action.options,
        service: services[action.service.internalName],
      }),
    };
  });

  return indexActionsByPublicId(scrubbedActions);
}

export function scrubServiceCredentials({
  actions,
  services,
}: {
  actions: Record<string, Action>;
  services: ServicesByName;
}) {
  const actionsWithNoCredentials: Record<string, Action> = {};
  const impactedServiceActions: string[] = [];
  Object.values(actions).forEach((action: Action) => {
    if (action.type === ActionType.SERVICE) {
      const matchingCredSchema = services[action.service.internalName].credentialsSchema;
      if (matchingCredSchema) {
        matchingCredSchema.credential_format?.secrets?.forEach(secretKey => {
          if (action.options?.[secretKey]) {
            action.options[secretKey].value = '';
            if (!impactedServiceActions.includes(action.service.name)) {
              impactedServiceActions.push(action.service.name);
            }
          }
        });
      }
    }
    actionsWithNoCredentials[action.publicId] = action;
  });

  return {
    actionsWithNoCredentials,
    impactedServiceActions,
  };
}
// remove default settings from serviceConfig on copy/save as they may contain secrets
// TODO - BUILD-4589 - update when we resolve copy/paste for default settings
export function scrubServiceDefaultValues(
  services: ServiceObject[] | ServiceObject): ServiceObject[] {
  const servicesArray = Array.isArray(services) ? services : [services];
  return servicesArray.map(service => ({
    ...service,
    serviceConfig: makeServiceConfig({
      serviceConfigOptions: service.serviceConfig?.serviceConfigOptions ?? [],
    }),
  }));
}

// BUILD-4687: `convertParsedOptionToActionOption`, `resolveDefaultOptionsForAction`,
// and `resolveServiceConfigDefaultsIntoActions` are temporarily disabled.
// Re-enable once a batched getServiceConfig endpoint is available (BUILD-4645).
// function convertParsedOptionToActionOption(parsedOption: ParsedOption): ActionOption {
//   return {
//     publicId: generatePublicId('actionoption'),
//     value: parsedOption.value,
//     jsonValue: null,
//     serviceConfigOptionId: parsedOption.serviceConfigOptionId,
//     selectedValues: parsedOption.selectedValues,
//   };
// }
//
// /**
//  * Resolves default service config options from the customer and Alloy hierarchy
//  * levels into the action's existing options. Only adds options that are not already
//  * explicitly set at the workflow level.
//  *
//  * Hierarchy priority: workflow-level (already in action.options) > customer > Alloy
//  */
// export function resolveDefaultOptionsForAction(args: {
//   existingOptions: Record<string, ActionOption>;
//   serviceConfig: ServiceConfig;
// }): Record<string, ActionOption> {
//   const { existingOptions, serviceConfig } = args;
//
//   // map service config option ids to slugs
//   const slugById: Record<number, string> = {};
//   serviceConfig.serviceConfigOptions?.forEach(opt => {
//     slugById[opt.serviceConfigOptionId] = opt.slug;
//   });
//
//   // collect existing option ids and slugs
//   const existingOptionIds = new Set<number>();
//   Object.values(existingOptions).forEach(option => {
//     if (option.serviceConfigOptionId) {
//       existingOptionIds.add(option.serviceConfigOptionId);
//     }
//   });
//   const existingSlugs = new Set<string>(Object.keys(existingOptions));
//
//   const newOptions: Record<string, ActionOption> = { ...existingOptions };
//   const addedOptionIds = new Set<number>();
//
//   // add customer-level options if not already added
//   serviceConfig.selectedCustomerOptions?.serviceConfigOptions?.forEach(parsedOption => {
//     if (existingOptionIds.has(parsedOption.serviceConfigOptionId)) return;
//     const slug = slugById[parsedOption.serviceConfigOptionId];
//     if (!slug || existingSlugs.has(slug)) return;
//
//     newOptions[slug] = convertParsedOptionToActionOption(parsedOption);
//     addedOptionIds.add(parsedOption.serviceConfigOptionId);
//   });
//
//   // add alloy-level options if not already added
//   serviceConfig.selectedAlloyOptions?.serviceConfigOptions?.forEach(parsedOption => {
//     if (existingOptionIds.has(parsedOption.serviceConfigOptionId)) return;
//     if (addedOptionIds.has(parsedOption.serviceConfigOptionId)) return;
//     const slug = slugById[parsedOption.serviceConfigOptionId];
//     if (!slug || existingSlugs.has(slug)) return;
//
//     newOptions[slug] = convertParsedOptionToActionOption(parsedOption);
//   });
//
//   return newOptions;
// }
//
// /**
//  * Materializes the service config defaults (customer and Alloy level) into each action's options.
//  * This ensures that when a workflow is copied to a different account, the effective
//  * service configuration is preserved as explicit overrides.
//  */
// export function resolveServiceConfigDefaultsIntoActions(args: {
//   actions: Record<string, Action>;
//   services: ServicesByName;
// }): Record<string, Action> {
//   const { actions, services } = args;
//
//   const resolvedActions: Record<string, Action> = {};
//   Object.entries(actions).forEach(([publicId, action]) => {
//     if (!isActionOfType(action, ActionType.SERVICE)) {
//       resolvedActions[publicId] = action;
//       return;
//     }
//
//     const serviceConfig = services[action.service.internalName]?.serviceConfig;
//     if (!serviceConfig) {
//       resolvedActions[publicId] = action;
//       return;
//     }
//
//     resolvedActions[publicId] = {
//       ...action,
//       options: resolveDefaultOptionsForAction({
//         existingOptions: action.options,
//         serviceConfig,
//       }),
//     };
//   });
//
//   return resolvedActions;
// }
